import React, { useState } from 'react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { MessageSquareWarning, FileWarning, Lightbulb, AlertTriangle, Send, Zap, ChevronLeft } from 'lucide-react';

const reportTypes = [
  { key: 'feedback', label: 'Feedback / Complaint / Demand', icon: MessageSquareWarning, color: 'text-gold' },
  { key: 'incident', label: 'Incident Report', icon: FileWarning, color: 'text-red-400' },
  { key: 'improvement', label: 'Improvement Advice', icon: Lightbulb, color: 'text-emerald-400' },
  { key: 'urgent', label: 'Urgent Report', icon: AlertTriangle, color: 'text-amber-400' },
];

const feedbackCategories = ['Customer Feedback', 'Customer Complaint', 'Customer Demand', 'Staff Feedback', 'Other'];
const improvementAreas = ['Operations', 'Menu', 'Service', 'Facility', 'Technology', 'Staff Training', 'Other'];
const priorityOptions = [{ value: 'low', label: 'Low' }, { value: 'medium', label: 'Medium' }, { value: 'high', label: 'High' }, { value: 'urgent', label: 'Urgent' }];

export default function QuickLinksModal({ open, onClose }) {
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ subject: '', description: '', priority: 'medium', extra_fields: {} });

  const reset = () => { setForm({ subject: '', description: '', priority: 'medium', extra_fields: {} }); setSelected(null); };
  const setExtra = (k, v) => setForm(p => ({ ...p, extra_fields: { ...p.extra_fields, [k]: v } }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.description.trim()) { toast.error('Description is required'); return; }
    setSubmitting(true);
    try {
      await api.post('/quick-reports', { report_type: selected, ...form });
      toast.success('Report submitted! Management notified.');
      reset();
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  const handleClose = () => { reset(); onClose(); };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) handleClose(); }}>
      <DialogContent aria-describedby={undefined} className="bg-bg-secondary border border-white/10 max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-white flex items-center gap-2">
            <Zap className="w-5 h-5 text-gold" /> Quick Links
          </DialogTitle>
        </DialogHeader>

        {!selected ? (
          <div className="grid grid-cols-2 gap-3 pt-2">
            {reportTypes.map(rt => {
              const Icon = rt.icon;
              return (
                <button key={rt.key} onClick={() => setSelected(rt.key)} className="p-4 bg-white/[0.03] border border-white/5 rounded-sm text-left hover:border-gold/20 transition-colors" data-testid={`ql-modal-${rt.key}`}>
                  <Icon className={`w-5 h-5 ${rt.color} mb-2`} />
                  <p className="text-white font-sans text-xs font-medium">{rt.label}</p>
                </button>
              );
            })}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3 pt-2">
            <button type="button" onClick={() => setSelected(null)} className="flex items-center gap-1 text-zinc-500 hover:text-zinc-300 font-sans text-xs" data-testid="ql-modal-back">
              <ChevronLeft className="w-3 h-3" /> Back
            </button>
            <Input value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))} className="bg-transparent border border-white/10 text-white font-sans text-sm" placeholder="Subject" data-testid="ql-subject" />
            {selected === 'feedback' && (
              <Select value={form.extra_fields.category || ''} onValueChange={v => setExtra('category', v)}>
                <SelectTrigger className="bg-transparent border border-white/10 text-white font-sans text-sm"><SelectValue placeholder="Category" /></SelectTrigger>
                <SelectContent className="bg-bg-secondary border border-white/10">{feedbackCategories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            )}
            {selected === 'incident' && (
              <>
                <Input type="date" value={form.extra_fields.incident_date || ''} onChange={e => setExtra('incident_date', e.target.value)} className="bg-transparent border border-white/10 text-white font-sans text-sm" data-testid="ql-incident-date" />
                <Input value={form.extra_fields.location || ''} onChange={e => setExtra('location', e.target.value)} className="bg-transparent border border-white/10 text-white font-sans text-sm" placeholder="Location" />
                <Input value={form.extra_fields.people_involved || ''} onChange={e => setExtra('people_involved', e.target.value)} className="bg-transparent border border-white/10 text-white font-sans text-sm" placeholder="People involved" />
              </>
            )}
            {selected === 'improvement' && (
              <Select value={form.extra_fields.area || ''} onValueChange={v => setExtra('area', v)}>
                <SelectTrigger className="bg-transparent border border-white/10 text-white font-sans text-sm"><SelectValue placeholder="Area" /></SelectTrigger>
                <SelectContent className="bg-bg-secondary border border-white/10">{improvementAreas.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
              </Select>
            )}
            <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} className="w-full bg-transparent border border-white/10 text-white font-sans text-sm rounded-sm p-3 min-h-[100px] focus:border-gold/50 focus:outline-none resize-none" placeholder="Description *" required data-testid="ql-description" />
            {selected !== 'urgent' && (
              <Select value={form.priority} onValueChange={v => setForm(p => ({ ...p, priority: v }))}>
                <SelectTrigger className="bg-transparent border border-white/10 text-white font-sans text-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-bg-secondary border border-white/10">{priorityOptions.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
              </Select>
            )}
            {selected === 'urgent' && (
              <p className="text-red-400 font-sans text-xs flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Flagged as URGENT</p>
            )}
            <Button type="submit" disabled={submitting} className="w-full bg-gold text-black font-bold rounded-sm font-sans text-sm" data-testid="ql-submit">
              {submitting ? <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" /> : <><Send className="w-4 h-4 mr-2" /> Submit</>}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
